import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";

const SITE_URL = "https://adrise-agency.com";

const CONTENT = {
    en: {
        title: "Refund Policy",
        subtitle: "Manual review framework for refund and credit adjustment requests.",
        seoTitle: "Adrise Refund Policy — Manual Review for Crypto Payments",
        seoDescription:
            "Adrise applies case-by-case manual review for refund requests. Review criteria include transaction status, operational usage, compliance findings, and settlement stage.",
        lead:
            "Adrise does not provide automatic refunds. Every refund or credit adjustment request is reviewed manually by finance and risk operations.",
        bullets: [
            "Blockchain transaction status and confirmation stage",
            "Whether platform services or account provisioning has already started",
            "Consumed service value and already-applied top-up fees",
            "Compliance flags, abuse indicators, or policy violations",
            "Duplicate payments or clear technical settlement errors",
        ],
        processTitle: "How to request a review",
        processSteps: [
            "Contact support via email or Telegram with your order ID and transaction hash.",
            "Provide reason for request and any supporting evidence.",
            "Adrise finance/risk team completes internal review and responds with outcome.",
            "If approved, refund or wallet credit adjustment is processed per operational schedule.",
        ],
        final:
            "Adrise reserves the right to reject requests where services were already consumed or where risk/compliance concerns are identified.",
    },
    pt: {
        title: "Política de Reembolso",
        subtitle: "Modelo de análise manual para pedidos de reembolso e ajuste de crédito.",
        seoTitle: "Adrise Política de Reembolso — Revisão Manual em Cripto",
        seoDescription:
            "A Adrise aplica revisão manual caso a caso para pedidos de reembolso. Critérios incluem status da transação, uso operacional, compliance e estágio de liquidação.",
        lead:
            "A Adrise não oferece reembolso automático. Todo pedido de reembolso ou ajuste de crédito é analisado manualmente por operações financeiras e de risco.",
        bullets: [
            "Status da transação blockchain e estágio de confirmação",
            "Se serviços/plataforma ou provisionamento de conta já iniciaram",
            "Uso do período de assinatura e valor de serviço já consumido",
            "Sinais de compliance, abuso ou violação de políticas",
            "Pagamentos duplicados ou erros técnicos claros de liquidação",
        ],
        processTitle: "Como solicitar revisão",
        processSteps: [
            "Contate suporte por e-mail ou Telegram com ID do pedido e hash da transação.",
            "Informe o motivo e evidências de suporte.",
            "O time financeiro/risco da Adrise conclui a análise e responde com o resultado.",
            "Se aprovado, o reembolso ou ajuste de crédito é processado conforme agenda operacional.",
        ],
        final:
            "A Adrise reserva-se o direito de rejeitar pedidos quando o serviço já foi consumido ou houver risco/compliance.",
    },
    es: {
        title: "Política de Reembolso",
        subtitle: "Marco de revisión manual para solicitudes de reembolso y ajuste de crédito.",
        seoTitle: "Adrise Política de Reembolso — Revisión Manual en Cripto",
        seoDescription:
            "Adrise aplica revisión manual caso por caso. Los criterios incluyen estado de transacción, uso operativo, compliance y etapa de liquidación.",
        lead:
            "Adrise no ofrece reembolsos automáticos. Toda solicitud de reembolso o ajuste de saldo se revisa manualmente por finanzas y riesgo.",
        bullets: [
            "Estado de la transacción blockchain y etapa de confirmación",
            "Si el servicio/provisión de cuenta ya comenzó",
            "Uso del periodo de suscripción y valor consumido",
            "Señales de compliance, abuso o infracciones de política",
            "Pagos duplicados o errores técnicos claros de conciliación",
        ],
        processTitle: "Cómo solicitar revisión",
        processSteps: [
            "Contacta soporte por email o Telegram con ID de pedido y hash de transacción.",
            "Indica motivo de la solicitud y evidencia de respaldo.",
            "El equipo de finanzas/riesgo de Adrise evalúa el caso y responde resultado.",
            "Si se aprueba, el reembolso o ajuste de crédito se procesa según la operación.",
        ],
        final:
            "Adrise se reserva el derecho de rechazar solicitudes cuando el servicio ya fue consumido o exista riesgo/compliance.",
    },
    ru: {
        title: "Политика возврата",
        subtitle: "Ручная проверка заявок на возврат и корректировку баланса.",
        seoTitle: "Adrise Политика возврата — Ручная проверка крипто-платежей",
        seoDescription:
            "Adrise рассматривает возвраты в ручном режиме по каждому случаю. Учитываются статус транзакции, этап оказания услуги, compliance и стадия расчетов.",
        lead:
            "Adrise не предоставляет автоматические возвраты. Каждая заявка на возврат или корректировку кредита рассматривается финансовой и риск-командой вручную.",
        bullets: [
            "Статус блокчейн-транзакции и этап подтверждений",
            "Начато ли уже оказание услуги/выдача аккаунта",
            "Фактическое потребление подписки и объема сервиса",
            "Флаги compliance, признаки злоупотреблений, нарушения",
            "Дубликат платежа или явная техническая ошибка сверки",
        ],
        processTitle: "Как подать запрос",
        processSteps: [
            "Свяжитесь с поддержкой по email или Telegram, укажите ID заказа и tx hash.",
            "Опишите причину запроса и приложите подтверждающие материалы.",
            "Команда финансов/рисков Adrise завершит проверку и сообщит решение.",
            "При одобрении выполняется возврат или корректировка баланса по операционному графику.",
        ],
        final:
            "Adrise оставляет за собой право отклонить запрос, если услуга уже потреблена или выявлены risk/compliance факторы.",
    },
    tr: {
        title: "İade Politikası",
        subtitle: "İade ve bakiye düzeltme talepleri için manuel inceleme çerçevesi.",
        seoTitle: "Adrise İade Politikası — Kripto Ödemelerde Manuel İnceleme",
        seoDescription:
            "Adrise iade taleplerini otomatik değil, vaka bazlı manuel inceler. Değerlendirme kriterleri: işlem durumu, hizmet kullanımı, uyum bulguları ve mutabakat aşaması.",
        lead:
            "Adrise otomatik iade uygulamaz. Her iade veya bakiye düzeltme talebi, finans ve risk ekipleri tarafından manuel olarak değerlendirilir.",
        bullets: [
            "Blockchain işleminin durumu ve onay aşaması",
            "Hizmetin veya hesap tahsis sürecinin başlayıp başlamadığı",
            "Tüketilmiş hizmet değeri ve uygulanmış yükleme komisyonları",
            "Uyum riskleri, suistimal göstergeleri veya politika ihlalleri",
            "Mükerrer ödeme veya açık teknik mutabakat hatası",
        ],
        processTitle: "İnceleme talebi nasıl açılır",
        processSteps: [
            "Sipariş ID ve işlem hash bilgisiyle e-posta veya Telegram üzerinden destekle iletişime geçin.",
            "Talep gerekçenizi ve varsa kanıtları paylaşın.",
            "Adrise finans/risk ekibi iç incelemeyi tamamlayıp sonucu bildirir.",
            "Onaylanan taleplerde iade veya cüzdan bakiyesi düzeltmesi operasyon planına göre uygulanır.",
        ],
        final:
            "Hizmetin tüketildiği veya risk/uyum kaygısı oluştuğu durumlarda talep reddedilebilir.",
    },
};

export default function RefundPage() {
    const { language } = useLanguage();
    const c = CONTENT[language] || CONTENT.en;

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: c.title,
        url: `${SITE_URL}/refund`,
        inLanguage: language,
        isPartOf: { "@type": "WebSite", name: "Adrise", url: SITE_URL },
    };

    return (
        <main data-testid="refund-page" className="bg-background text-foreground min-h-screen">
            <SEOHead
                title={c.seoTitle}
                description={c.seoDescription}
                canonical={`${SITE_URL}/refund`}
                keywords={[
                    "adrise refund policy",
                    "manual review refund",
                    "crypto payment dispute",
                    "wallet credit adjustment",
                ]}
                jsonLd={jsonLd}
                alternates={[
                    { hrefLang: "en", href: `${SITE_URL}/refund` },
                    { hrefLang: "pt", href: `${SITE_URL}/refund` },
                    { hrefLang: "es", href: `${SITE_URL}/refund` },
                    { hrefLang: "ru", href: `${SITE_URL}/refund` },
                    { hrefLang: "tr", href: `${SITE_URL}/refund` },
                    { hrefLang: "x-default", href: `${SITE_URL}/refund` },
                ]}
            />

            <Navbar />

            <section className="pt-32 md:pt-40 pb-16" data-testid="refund-hero-section">
                <div className="container-skala">
                    <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                        {c.title}
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground mt-4 max-w-3xl">
                        {c.subtitle}
                    </p>
                </div>
            </section>

            <section className="pb-20" data-testid="refund-content-section">
                <div className="container-skala max-w-4xl space-y-7">
                    <article className="bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid="refund-lead-card">
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{c.lead}</p>
                    </article>

                    <article className="bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid="refund-review-criteria-card">
                        <h2 className="text-base md:text-lg font-semibold tracking-tight">{language === "tr" ? "İnceleme kriterleri" : language === "ru" ? "Критерии проверки" : language === "es" ? "Criterios de revisión" : language === "pt" ? "Critérios de análise" : "Review criteria"}</h2>
                        <ul className="list-disc pl-5 mt-3 space-y-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                            {c.bullets.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                    </article>

                    <article className="bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid="refund-process-card">
                        <h2 className="text-base md:text-lg font-semibold tracking-tight">{c.processTitle}</h2>
                        <ol className="list-decimal pl-5 mt-3 space-y-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                            {c.processSteps.map((step) => (
                                <li key={step}>{step}</li>
                            ))}
                        </ol>
                    </article>

                    <article className="bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid="refund-final-note-card">
                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{c.final}</p>
                    </article>
                </div>
            </section>

            <FloatingContact />
            <Footer />
        </main>
    );
}
